import ICON_LUXURY from "../assets/trip-luxury.svg";
import ICON_FAMILY from "../assets/trip-family.svg";
import ICON_FOODIE from "../assets/trip-foodie.svg";
import ICON_ADVENTURE from "../assets/trip-adventure.svg";
import ICON_CULTURE from "../assets/trip-culture.svg";
import ICON_WORK from "../assets/trip-work.svg";
import ICON_GENERIC from "../assets/trip-generic.svg";
import ICON_BUDGET from "../assets/trip-budget.svg";
import ICON_CREATOR from "../assets/trip-creator.png";
import Image from "@/components/AppImage/AppImage";
import type { ReactElement } from "react";
import { TravellerType } from "@/models/travellerType";
import { TravelWithType } from "@/models/travelWithType";

type TripType = {
  id: string;
  label: string;
  icon: string;
};

type TravellerTypeConfig = {
  id: string;
  label: TravellerType;
  icon: string;
  enumValue: TravellerType;
};

type TravelWithTypeConfig = {
  id: string;
  label: TravelWithType;
  icon: string;
  enumValue: TravelWithType;
};

type TravellerTypeUI = {
  id: string;
  label: TravellerType;
  enumValue: TravellerType;
  icon: ReactElement;
};

type TravelWithTypeUI = {
  id: string;
  label: TravelWithType;
  enumValue: TravelWithType;
  icon: ReactElement;
};

// Trip types for posts (itinerary/list creation) - 7 types (Digital Nomad removed)
// Family moved to Travel With, Budget now uses symbols ($, $$, $$$)
const TRIP_TYPES: TripType[] = [
  { id: "luxury", label: "Luxury", icon: ICON_LUXURY },
  { id: "foodie", label: "Foodie", icon: ICON_FOODIE },
  { id: "adventure", label: "Adventure", icon: ICON_ADVENTURE },
  { id: "culture", label: "Culture", icon: ICON_CULTURE },
  { id: "work", label: "Work", icon: ICON_WORK },
  { id: "wellness", label: "Wellness", icon: ICON_GENERIC },
  { id: "celebration", label: "Celebration", icon: ICON_GENERIC },
];

export const TRIP_TYPES_UI = TRIP_TYPES.map((tripType) => ({
  ...tripType,
  icon: <Image src={tripType.icon} alt={`${tripType.label} icon`} width={24} height={24} className="pill-img-icon" />,
}));

// Traveller types for user profile (matches React Native; Creator uses icon_traveller_type_creator)
// Family removed (moved to Travel With), Wellness added
const TRAVELLER_TYPES: TravellerTypeConfig[] = [
  { id: "digital-nomad", label: TravellerType.DigitalNomad, icon: ICON_WORK, enumValue: TravellerType.DigitalNomad },
  { id: "luxury", label: TravellerType.Luxury, icon: ICON_LUXURY, enumValue: TravellerType.Luxury },
  { id: "foodie", label: TravellerType.Foodie, icon: ICON_FOODIE, enumValue: TravellerType.Foodie },
  { id: "adventure", label: TravellerType.Adventure, icon: ICON_ADVENTURE, enumValue: TravellerType.Adventure },
  { id: "culture", label: TravellerType.Culture, icon: ICON_CULTURE, enumValue: TravellerType.Culture },
  { id: "creator", label: TravellerType.Creator, icon: ICON_CREATOR, enumValue: TravellerType.Creator },
  { id: "wellness", label: TravellerType.Wellness, icon: ICON_GENERIC, enumValue: TravellerType.Wellness },
];

export const TRAVELLER_TYPES_UI: TravellerTypeUI[] = TRAVELLER_TYPES.map((travellerType) => ({
  id: travellerType.id,
  label: travellerType.label,
  enumValue: travellerType.enumValue,
  icon: <Image src={travellerType.icon} alt={`${travellerType.label} icon`} width={24} height={24} className="pill-img-icon" />,
}));

/**
 * Match API / profile payload strings to traveller type UI (icons + canonical label).
 * Mirrors React Native profile traveller pills (enum labels + slug ids).
 */
export function resolveTravellerTypeUi(raw: string): TravellerTypeUI | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const lower = trimmed.toLowerCase();
  const slug = lower.replace(/[\s_]+/g, '-');

  return TRAVELLER_TYPES_UI.find((t) => {
    if (t.id === slug || t.id === lower) return true;
    if (t.label.toLowerCase() === lower) return true;
    if (String(t.enumValue).toLowerCase() === lower) return true;
    return false;
  });
}

// Travel with types for user profile (4 types matching React Native)
const TRAVEL_WITH_TYPES: TravelWithTypeConfig[] = [
  { id: "solo", label: TravelWithType.Solo, icon: ICON_GENERIC, enumValue: TravelWithType.Solo },
  { id: "couple", label: TravelWithType.Couple, icon: ICON_GENERIC, enumValue: TravelWithType.Couple },
  { id: "friends", label: TravelWithType.Friends, icon: ICON_GENERIC, enumValue: TravelWithType.Friends },
  { id: "family", label: TravelWithType.Family, icon: ICON_FAMILY, enumValue: TravelWithType.Family },
];

export const TRAVEL_WITH_TYPES_UI: TravelWithTypeUI[] = TRAVEL_WITH_TYPES.map((travelWithType) => ({
  id: travelWithType.id,
  label: travelWithType.label,
  enumValue: travelWithType.enumValue,
  icon: <Image src={travelWithType.icon} alt={`${travelWithType.label} icon`} width={24} height={24} className="pill-img-icon" />,
}));

/** Budget pills for create-list / preferences ($, $$, $$$) */
export const CREATE_LIST_BUDGET_OPTIONS: { id: string; label: string }[] = [
  { id: '$', label: '$' },
  { id: '$$', label: '$$' },
  { id: '$$$', label: '$$$' },
];

/** Same SVG assets as list / trip preferences (Create List); 20×20 per Figma Recommend tags. */
const RECOMMENDATION_PREFERENCE_ICON_SRC: Record<string, typeof ICON_LUXURY> = {
  luxury: ICON_LUXURY,
  foodie: ICON_FOODIE,
  adventure: ICON_ADVENTURE,
  culture: ICON_CULTURE,
  work: ICON_WORK,
  wellness: ICON_GENERIC,
  celebration: ICON_GENERIC,
  budget: ICON_BUDGET,
  family: ICON_FAMILY,
};

export function RecommendationPreferenceTagIcon({
  tagId,
  size = 20,
}: {
  tagId: string;
  size?: number;
}): ReactElement {
  const src = RECOMMENDATION_PREFERENCE_ICON_SRC[tagId] ?? ICON_GENERIC;
  return (
    <Image src={src} alt="" width={size} height={size} className="shrink-0 object-contain" />
  );
}

